import { Models } from "appwrite";
import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import {
  useLikePost,
  useSavePost,
  useGetCurrentUser,
} from "@/lib/react-query/queries";
import { Button } from "@/components/ui/button";
import { createDealStatus, getDealByBuyerAndSeller } from "@/lib/appwrite/api";
import { useUserContext } from "@/context/AuthContext";
import { MessageCircle, Send, X, ShoppingBag, HelpCircle, Trash2 } from 'lucide-react';
import { appwriteConfig, databases } from '@/lib/appwrite/config';
import { Query, ID } from 'appwrite';
import { buildSignInRedirect } from '@/lib/auth/redirect';

type PostStatsProps = {
  post: Models.Document;
  userId: string;
  postCreatorId: string;
};

interface Comment {
  id: string;
  userId: string;
  userName: string;
  text: string;
  userRole: string;
  timestamp: string;
  priority?: 'normal' | 'question' | 'purchase';
}

const PostStats = ({ post, userId, postCreatorId }: PostStatsProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useUserContext();
  const postId = post.$id ?? (post as any).id ?? "";

  // Keep array support when backend sends it, but also support count-based payloads.
  const likesList = useMemo<string[]>(
    () => (Array.isArray((post as any).likedBy) ? (post as any).likedBy : []),
    [(post as any).likedBy]
  );
  const savedList = useMemo<string[]>(
    () => (Array.isArray((post as any).savedBy) ? (post as any).savedBy : []),
    [(post as any).savedBy]
  );

  const [likes, setLikes] = useState<string[]>(likesList);
  const [likesCount, setLikesCount] = useState<number>(
    Number((post as any).likesCount ?? (post as any).likes ?? likesList.length ?? 0)
  );
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [savedBy, setSavedBy] = useState<string[]>(savedList);
  const [isDealLoading, setIsDealLoading] = useState(false);
  const likeOverrideRef = useRef<boolean | null>(null);
  const saveOverrideRef = useRef<boolean | null>(null);

  // Comment states
  const [isCommentsOpen, setIsCommentsOpen] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { mutate: likePost } = useLikePost();
  const { mutate: savePost } = useSavePost();
  const { data: currentUser } = useGetCurrentUser();
  const currentUserId = currentUser?.$id ?? userId;
  const databaseId = appwriteConfig.databaseId;
  const commentsCollectionId = appwriteConfig.postComments; // Make sure to add this to your config

  const { data: likedPosts = [] } = useQuery({
    queryKey: ["likedPosts", currentUserId],
    queryFn: async () => {
      if (!currentUserId) return [];
      const response = await databases.listDocuments(
        databaseId,
        appwriteConfig.postCollectionId,
        [Query.contains("likedBy", currentUserId)]
      );
      return response.documents ?? [];
    },
    enabled: !!currentUserId,
    staleTime: 1000 * 60 * 5,
  });

  const { data: savedPosts = [] } = useQuery({
    queryKey: ["savedPosts", currentUserId],
    queryFn: async () => {
      if (!currentUserId) return [];
      const response = await databases.listDocuments(
        databaseId,
        appwriteConfig.postCollectionId,
        [Query.contains("savedBy", currentUserId)]
      );
      return response.documents ?? [];
    },
    enabled: !!currentUserId,
    staleTime: 1000 * 60 * 5,
  });

  // Quick replies for comments
  const quickReplies = [
    { icon: '👍', text: 'Interested!', type: 'interest' },
    { icon: '💯', text: 'Love this!', type: 'praise' },
    { icon: '❓', text: 'Price?', type: 'question' },
    { icon: '📦', text: 'Available?', type: 'purchase' },
  ];

  const detectMessagePriority = (text: string): 'normal' | 'question' | 'purchase' => {
    const lowerText = text.toLowerCase();
    
    const purchaseKeywords = [
      'buy', 'purchase', 'order', 'cart', 'checkout', 'price', 'cost', 
      'how much', 'available', 'stock', 'quantity', 'shipping', 'delivery',
      'payment', 'pay', 'sell', 'discount', 'offer', 'deal', 'rupees', '₹',
      'rs', 'inr', 'money', 'expensive', 'cheap', 'afford', 'book', 'reserve'
    ];
    
    if (purchaseKeywords.some(keyword => lowerText.includes(keyword))) {
      return 'purchase';
    }
    
    const questionKeywords = [
      '?', 'how', 'what', 'when', 'where', 'why', 'can', 'could', 'would', 
      'is', 'are', 'will', 'does', 'do', 'which', 'who', 'whose', 'help',
      'explain', 'tell', 'show', 'demo', 'review', 'opinion', 'think',
      'recommend', 'suggest', 'advice', 'guide', 'tutorial'
    ];
    
    if (questionKeywords.some(keyword => lowerText.includes(keyword))) {
      return 'question';
    }
    
    return 'normal';
  };

  const getPriorityStyle = (priority: string, isCurrentUser: boolean) => {
    if (isCurrentUser) {
      return 'ml-auto bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md';
    }
    
    switch (priority) {
      case 'purchase':
        return 'mr-auto bg-gradient-to-r from-green-50 to-emerald-50 text-green-800 border border-green-200 shadow-sm';
      case 'question':
        return 'mr-auto bg-gradient-to-r from-orange-50 to-amber-50 text-orange-800 border border-orange-200 shadow-sm';
      default:
        return 'mr-auto bg-gray-100 text-gray-800 border border-gray-200';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'purchase':
        return <ShoppingBag size={12} className="text-green-600" />;
      case 'question':
        return <HelpCircle size={12} className="text-orange-600" />;
      default:
        return <MessageCircle size={12} className="text-gray-500" />;
    }
  };

  const loadCommentCount = async () => {
  try {
    const response = await databases.listDocuments(
      databaseId,
      commentsCollectionId,
      [
        Query.equal("postId", postId),
        Query.limit(1)
      ]
    );
    
    setCommentCount(response.total);
  } catch (error) {
    console.error("Failed to load comment count:", error);
    setCommentCount(0);
  }
  };

  useEffect(() => {
    loadCommentCount();
}, [postId]);

  useEffect(() => {
    setLikes(likesList);
    setSavedBy(savedList);
    setLikesCount(Number((post as any).likesCount ?? (post as any).likes ?? likesList.length ?? 0));
  }, [postId, (post as any).likesCount, (post as any).likes, likesList, savedList]);

  useEffect(() => {
    likeOverrideRef.current = null;
    saveOverrideRef.current = null;
  }, [postId, currentUserId]);

  useEffect(() => {
    const hasLiked = likesList.length > 0
      ? likesList.includes(currentUserId)
      : likedPosts.some((likedPost: any) => (likedPost.$id ?? likedPost.id) === postId);

    const hasSaved = savedList.length > 0
      ? savedList.includes(currentUserId)
      : savedPosts.some((savedPost: any) => (savedPost.$id ?? savedPost.id) === postId);

    if (likeOverrideRef.current === null || likeOverrideRef.current === hasLiked) {
      setIsLiked(hasLiked);
      likeOverrideRef.current = null;
    }

    if (saveOverrideRef.current === null || saveOverrideRef.current === hasSaved) {
      setIsSaved(hasSaved);
      saveOverrideRef.current = null;
    }
  }, [currentUserId, likedPosts, savedPosts, postId, likesList, savedList]);

  // Load comments when modal opens
  useEffect(() => {
    if (isCommentsOpen) {
      loadComments();
      inputRef.current?.focus();
    }
  }, [isCommentsOpen]);

  // Auto scroll to bottom when new comments arrive
  useEffect(() => {
    if (messagesEndRef.current && isCommentsOpen) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [comments, isCommentsOpen]);

  const loadComments = async () => {
    try {
      const response = await databases.listDocuments(
        databaseId,
        commentsCollectionId,
        [
          Query.equal("postId", postId),
          Query.orderDesc("timestamp"),
          Query.limit(50)
        ]
      );
      
      const loadedComments = response.documents.map(doc => ({
        id: doc.$id,
        userId: doc.userId,
        userName: doc.userName,
        text: doc.text,
        userRole: doc.userRole,
        timestamp: doc.timestamp,
        priority: detectMessagePriority(doc.text)
      })).reverse();
      
      setComments(loadedComments);
      setCommentCount(loadedComments.length);
    } catch (error) {
      console.error("Failed to load comments:", error);
      setCommentCount(0);
    }
  };

  const handleAuthRequired = () => {
    navigate(buildSignInRedirect(`${window.location.pathname}${window.location.search}`));
  };

  const handleLikePost = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation();
    
    if (!isAuthenticated) {
      handleAuthRequired();
      return;
    }

    const previousLikes = likes;
    const previousLikesCount = likesCount;
    const nextIsLiked = !isLiked;
    const newLikes = nextIsLiked
      ? Array.from(new Set([...likes, currentUserId]))
      : likes.filter((id) => id !== currentUserId);

    likeOverrideRef.current = nextIsLiked;
    setLikes(newLikes);
    setLikesCount(nextIsLiked ? previousLikesCount + 1 : Math.max(previousLikesCount - 1, 0));
    setIsLiked(nextIsLiked);

    likePost(
      { postId, likesArray: newLikes },
      {
        onError: () => {
          likeOverrideRef.current = isLiked;
          setLikes(previousLikes);
          setLikesCount(previousLikesCount);
          setIsLiked(isLiked);
        },
      }
    );
  };

  const handleSavePost = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation();
    
    if (!isAuthenticated) {
      handleAuthRequired();
      return;
    }

    const previousSavedBy = savedBy;
    const nextIsSaved = !isSaved;
    const newSavedBy = nextIsSaved
      ? Array.from(new Set([...savedBy, currentUserId]))
      : savedBy.filter((id) => id !== currentUserId);

    saveOverrideRef.current = nextIsSaved;
    setSavedBy(newSavedBy);
    setIsSaved(nextIsSaved);

    savePost(
      { postId, savedByArray: newSavedBy },
      {
        onError: () => {
          saveOverrideRef.current = isSaved;
          setSavedBy(previousSavedBy);
          setIsSaved(isSaved);
        },
      }
    );
  };

  const handleStartDeal = async (e: React.MouseEvent, postCreatorId: string) => {
    e.stopPropagation();

    if (!isAuthenticated) {
      handleAuthRequired();
      return;
    }

    setIsDealLoading(true);

    try {
      // Current user is always the buyer when clicking Deal button
      const buyerId = currentUser?.$id || currentUser?.id || userId;
      if (!buyerId) {
        throw new Error("No authenticated buyer was found.");
      }
      const sellerId = postCreatorId;

      // Check if an ACTIVE deal already exists for this buyer + seller + post
      // Note: getDealByBuyerAndSeller excludes completed deals
      const existingDeal = await getDealByBuyerAndSeller(buyerId, sellerId, postId) ||
                          await getDealByBuyerAndSeller(sellerId, buyerId, postId);

      const deal = existingDeal || await createDealStatus({
        buyerId,
        sellerId,
        postId,
        wasInitiated: true
      });

      if (!deal) {
        throw new Error("Unable to create the deal chat.");
      }

      navigate(`/chats/${postCreatorId}?postId=${postId}&dealId=${deal.$id}`);
    } catch (error) {
      console.error("Error starting deal:", error);
    } finally {
      setIsDealLoading(false);
    }
  };

  const handleSendComment = async (messageText?: string) => {
    const textToSend = messageText || newComment;
    if (!textToSend.trim()) return;

    if (!isAuthenticated) {
      handleAuthRequired();
      return;
    }

    try {
      // TODO: Add content moderation here if needed
      // const blockResult = isBlockedMessage(textToSend);
      // if (blockResult.isBlocked) { return; }

        await databases.createDocument(
          databaseId,
          commentsCollectionId,
          ID.unique(),
          {
            postId,
            userId: userId,
            userName: currentUser!.name,
            text: textToSend,
      userRole: currentUser!.accountType || 'buyer',
            timestamp: new Date().toISOString(),
          }
      );

      // Optimistically add to UI
      const newCommentObj: Comment = {
        id: Date.now().toString(),
        userId: userId,
        userName: currentUser!.name,
        text: textToSend,
        userRole: currentUser!.accountType || 'buyer',
        timestamp: new Date().toISOString(),
        priority: detectMessagePriority(textToSend)
      };

      setComments(prev => [...prev, newCommentObj]);
      setCommentCount(prev => prev + 1);
      setNewComment('');
      setIsTyping(false);
      
    } catch (error) {
      console.error("Error sending comment:", error);
    }
  };

  const handleQuickReply = (reply: { text: string; type: string }) => {
    handleSendComment(reply.text);
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await databases.deleteDocument(databaseId, commentsCollectionId, commentId);
      
      setComments(prev => prev.filter(c => c.id !== commentId));
      setCommentCount(prev => prev - 1);
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / 60000);
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const containerStyles = location.pathname.startsWith("/profile")
    ? "w-full"
    : "";

  return (
    <>
      <motion.div
        className={`flex justify-between items-center ${containerStyles} px-3 py-3 relative`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Like button - left aligned */}
        <motion.div 
          className="flex items-center gap-2 min-w-0 flex-shrink-0 mr-2"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <motion.button
            onClick={handleLikePost}
            className="relative p-1.5 rounded-full hover:bg-rose-500/10 transition-colors group flex-shrink-0"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <motion.svg
              className={`w-4 h-4 transition-colors ${
                isLiked
                  ? 'text-rose-500 fill-current'
                  : 'text-light-3 group-hover:text-rose-400'
              }`}
              fill={isLiked ? "currentColor" : "none"}
              stroke="currentColor"
              strokeWidth={isLiked ? 0 : 2}
              viewBox="0 0 24 24"
              initial={false}
              animate={isLiked ? { scale: [1, 1.2, 1] } : { scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </motion.svg>
          </motion.button>
          <motion.p 
            className="text-xs font-medium text-light-1 min-w-[1ch]"
            key={likesCount}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            {likesCount}
          </motion.p>
        </motion.div>

        {/* Comment button */}
        <motion.div 
          className="flex items-center gap-2 min-w-0 flex-shrink-0"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <motion.button
            onClick={(e) => {
              e.stopPropagation();
              if (!isAuthenticated) {
                handleAuthRequired();
                return;
              }
              setIsCommentsOpen(true);
            }}
            className="relative p-1.5 rounded-full hover:bg-blue-500/10 transition-colors group flex-shrink-0"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <MessageCircle className="w-4 h-4 text-light-3 group-hover:text-blue-500 transition-colors" />
          </motion.button>
          <motion.p 
            className="text-xs font-medium text-light-1 min-w-[1ch]"
            key={commentCount}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            {commentCount}
          </motion.p>
        </motion.div>

        {/* Start Deal button - center with proper spacing */}
        {post.isSellingPost && (
          <motion.div 
            className="flex-1 flex justify-center px-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Button
              size="sm"
              disabled={isDealLoading}
              className="bg-gradient-to-r from-blue-500/90 to-blue-600/90 hover:from-blue-500 hover:to-blue-600 text-white rounded-full px-3 py-1 text-xs font-medium shadow-sm border-0 transition-all duration-200 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed min-w-[70px] h-7"
              onClick={(e) => handleStartDeal(e, postCreatorId)}
            >
              <AnimatePresence mode="wait">
                {isDealLoading ? (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center gap-1"
                  >
                    <div className="w-2.5 h-2.5 border border-current border-t-transparent rounded-full animate-spin" />
                    <span className="text-xs">Deal...</span>
                  </motion.div>
                ) : (
                  <motion.div
                    key="start-deal"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center gap-1"
                  >
                    <span className="text-xs whitespace-nowrap">Deal</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </Button>
          </motion.div>
        )}

        {/* Save button - right aligned */}
        <motion.div
          className="flex-shrink-0"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <motion.button
            onClick={handleSavePost}
            className="relative p-1.5 rounded-full hover:bg-yellow-500/10 transition-colors group"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <motion.svg
              className={`w-4 h-4 transition-colors ${
                isSaved
                  ? 'text-yellow-500 fill-current'
                  : 'text-light-3 group-hover:text-yellow-400'
              }`}
              fill={isSaved ? "currentColor" : "none"}
              stroke="currentColor"
              strokeWidth={isSaved ? 0 : 2}
              viewBox="0 0 24 24"
              initial={false}
              animate={isSaved ? { scale: [1, 1.1, 1] } : { scale: 1 }}
              transition={{ duration: 0.2 }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
            </motion.svg>
          </motion.button>
        </motion.div>
      </motion.div>

      {/* Comments Modal */}
      <AnimatePresence>
        {isCommentsOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[55]"
              onClick={() => setIsCommentsOpen(false)}
            />

            {/* Modal */}
            <motion.div
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed inset-x-0 bottom-0 z-[60] bg-white rounded-t-3xl shadow-2xl max-h-[75vh] sm:max-h-[85vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-800">Comments</h3>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                    {commentCount}
                  </span>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setIsCommentsOpen(false)}
                  className="p-2 rounded-full hover:bg-gray-200/80 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </motion.button>
              </div>

              {/* Comments List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {comments.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                    <MessageCircle size={32} className="mb-2 opacity-50" />
                    <p className="text-sm font-medium">No comments yet</p>
                    <p className="text-xs">Be the first to comment!</p>
                  </div>
                ) : (
                  comments.map((comment, index) => {
                    const isCurrentUser = comment.userId === userId;
                    const priorityStyle = getPriorityStyle(comment.priority || 'normal', isCurrentUser);
                    
                    return (
                      <motion.div
                        key={`${comment.id}-${index}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="flex flex-col"
                      >
                        // Find the comment rendering section (around line 650) and replace it with:

                        <div className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm relative group ${priorityStyle}`}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center space-x-2">
                              {!isCurrentUser && getPriorityIcon(comment.priority || 'normal')}
                              <span className="font-semibold text-xs">
                                {isCurrentUser ? 'You' : comment.userName}
                              </span>
                              {comment.userRole === 'seller' && (
                                <span className="bg-rose-500 text-white text-xs px-2 py-0.5 rounded-full">
                                  Host
                                </span>
                              )}
                            </div>
                            {isCurrentUser && (
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => handleDeleteComment(comment.id)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-rose-100 rounded"
                              >
                                <Trash2 size={12} className="text-rose-500" />
                              </motion.button>
                            )}
                          </div>
                          
                          <div className="leading-relaxed break-words mb-1">
                            {comment.text}
                          </div>
                          
                          <div className="flex items-center justify-end">
                            <span className="text-xs opacity-60 group-hover:opacity-100 transition-opacity">
                              {formatTimestamp(comment.timestamp)}
                            </span>
                          </div>
                          
                          {!isCurrentUser && comment.priority !== 'normal' && (
                            <div className="absolute -top-1 -right-1">
                              <div className={`w-3 h-3 rounded-full ${
                                comment.priority === 'purchase' ? 'bg-green-500' : 'bg-orange-500'
                              } animate-pulse`}></div>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Quick Replies */}
              <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
                <div className="flex space-x-2 overflow-x-auto pb-2">
                  {quickReplies.map((reply, index) => (
                    <motion.button
                      key={index}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleQuickReply(reply)}
                      className="flex-shrink-0 flex items-center space-x-1 bg-white hover:bg-gray-100 px-3 py-2 rounded-full text-xs transition-all border border-gray-300 shadow-sm"
                    >
                      <span>{reply.icon}</span>
                      <span className="font-medium text-rose-400">{reply.text}</span>
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Input Area */}
              <div className="p-4 border-t border-gray-200 bg-white">
                <div className="flex items-center space-x-3">
                  <div className="flex-1 relative">
                    <input
                      ref={inputRef}
                      value={newComment}
                      onChange={(e) => {
                        setNewComment(e.target.value);
                        setIsTyping(e.target.value.length > 0);
                      }}
                      className="w-full bg-gray-100 text-gray-800 border border-gray-300 rounded-full px-4 py-3 pr-20 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      placeholder="Write a comment..."
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendComment();
                        }
                      }}
                      maxLength={200}
                    />
                    
                    <div className={`absolute right-12 top-1/2 transform -translate-y-1/2 text-xs transition-colors ${
                      newComment.length > 180 ? 'text-rose-500' : 
                      newComment.length > 150 ? 'text-yellow-500' : 'text-gray-400'
                    }`}>
                      {newComment.length}/300
                    </div>
                  </div>
                  
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleSendComment()}
                    disabled={!newComment.trim()}
                    className={`p-3 rounded-full transition-all ${
                      newComment.trim()
                        ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg' 
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <Send 
                      size={18} 
                      className={`transition-transform ${
                        newComment.trim() ? 'rotate-45' : ''
                      }`} 
                    />
                  </motion.button>
                </div>
                
                {isTyping && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-2 text-xs text-gray-500 flex items-center justify-between"
                  >
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce"></div>
                        <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-1 h-1 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <span>Typing...</span>
                    </div>
                    <span className="text-gray-400">Press Enter to send</span>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default PostStats;
